import { callFirebaseFunction } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

const blackList = [
  '123456qwe!',
  'Qwerty@123',
  'Qwerty123!',
  'Qwerty124!',
  'Abcd123!',
  'Bangbang123',
  'Password@123',
  'Password123!',
  'Password@1234',
  'Password123#',
];

const whiteList = [
  'Test1234!',
  'qwer1234',
  '12345678',
];

// eslint-disable-next-line import/prefer-default-export
export function usePasswordValidation() {
  async function isBlacklisted(password) {
    try {
      const hashedPassword = (await AppUtils.hashString(password, 'SHA-1')).toUpperCase();
      const prefix = hashedPassword.slice(0, 5);

      const suffixes = await callFirebaseFunction('isPasswordBreached', { prefix });

      if (suffixes) {
        const suffix = hashedPassword.slice(5);
        return suffixes.data.split('\n').map((line) => line.split(':')[0]).includes(suffix);
      }
    } catch (error) {
      logEvent(error);
      return blackList.find((blackListItem) => blackListItem.toLowerCase() === password.toLowerCase());
    }
  }

  function includesEmail(password, email) {
    const truncatedPassword = password?.trim() ?? '';
    const truncatedEmail = email?.trim() ?? '';

    if (truncatedPassword.length === 0 || truncatedEmail.length === 0) {
      return false;
    }
    const splitEmail = truncatedEmail.toLowerCase().split('@');
    return truncatedPassword.toLowerCase().includes(splitEmail[0]);
  }

  function isLongEnough(password) {
    return password.trim().length >= 8;
  }

  function hasUppercaseLatter(password) {
    return /[A-Z]/.test(password);
  }

  function hasLowercaseLatter(password) {
    return /[a-z]/.test(password);
  }

  function hasNumericCharacter(password) {
    return /[0-9]/.test(password);
  }

  function hasSpecialCharacter(password) {
    return /[^A-Za-z0-9]/.test(password);
  }

  // This check aims to simplify work in the development environment and will ONLY apply in development mode
  function isWhitelisted(password) {
    return ['development', 'staging'].includes(process.env.ENV_TYPE) && whiteList.includes(password.trim());
  }

  function getPasswordRules(email) {
    let isWhitelistedPassword = false;

    const rules = [
      (v) => {
        isWhitelistedPassword = isWhitelisted(v);
        return isWhitelistedPassword || true;
      },
      async (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        const breachedPassword = await isBlacklisted(v);
        return !breachedPassword || i18n.t(
          'signup.signupComponent.inputPasswordValidator.blackListError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        return !includesEmail(v, email) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.containsEmailError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }
        return isLongEnough(v) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.shortPasswordError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        return hasUppercaseLatter(v) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.noUppercaseLettersError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        return hasLowercaseLatter(v) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.noLowercaseLettersError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        return hasNumericCharacter(v) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.noNumericCharacterError',
        );
      },
      (v) => {
        if (isWhitelistedPassword) {
          return true;
        }

        return hasSpecialCharacter(v) || i18n.t(
          'signup.signupComponent.inputPasswordValidator.noSpecialCharacterError',
        );
      },
    ];

    return rules;
  }

  function evaluatePasswordStrength(password, email) {
    let score = 0;

    if (isLongEnough(password)) {
      score += 40;
    }

    if (hasUppercaseLatter(password)) {
      score += 5;
    }

    if (hasLowercaseLatter(password)) {
      score += 5;
    }

    if (hasNumericCharacter(password)) {
      score += 15;
    }

    if (hasSpecialCharacter(password)) {
      score += 25;
    }

    if (!includesEmail(password, email)) {
      score += 10;
    }

    return score;
  }

  return {
    getPasswordRules,
    evaluatePasswordStrength,
  };
}
